/**
 * Can this tenant create new items right now? — the SPA half of manual billing's
 * write freeze (docs/plans/2026-08-06_MANUAL_BILLING_MODE.md §12).
 *
 * ── Why a shared provider and not a fetch per screen ────────────────────
 *
 * Two unrelated surfaces need the same answer: the DRIVE (so an upload is
 * explained before it is attempted) and the CHAT (so an agent turn is warned
 * before its credits are spent — decision 9). They live in `features/files` and
 * `features/agent`, neither of which may import the other, so the honest home is
 * `shared/core-ui`, which both already depend on. Two fetches would also mean two
 * answers on one screen while a freeze was being lifted.
 *
 * ── Why this replaces the mechanism the plan proposed ───────────────────
 *
 * §5 proposed decision 9's warning as "a field on `CapCheckOk`, the enqueue
 * response, and A1's banner". That does not fit: the chat's enqueue response IS
 * the AG-UI SSE stream, not a JSON body, so there is no field to add to it. A
 * tenant-state read is strictly better anyway — it warns *before the user presses
 * send* rather than after the turn is accepted, which is what decision 9 actually
 * asks for ("before the turn runs, not after the credits are spent"), and the same
 * state serves the drive banner.
 *
 * ── Staleness, stated ───────────────────────────────────────────────────
 *
 * Fetched on mount and on tenant change, then refreshed on an interval that is
 * deliberately longer than the server's own 60 s freeze cache — polling faster
 * than the source of truth changes only adds requests. `refresh()` is the
 * escape hatch for the moment that matters: a 402 handler can call it so the
 * banner appears immediately after the first refused write, rather than the user
 * seeing a refusal with no explanation on screen.
 *
 * ── Two things converge faster than the poll, and why they had to ───────
 *
 * The interval's justification is the freeze's 60 s server cache, and it does
 * NOT transfer to identity-only (0175), which deliberately has no process cache
 * at all: enforcement is instant on the next request, so the only lag left was
 * this hook's. A platform admin flipping a live tenant left the person looking
 * at a stale shell for up to a poll interval, and — worse — a drive read
 * refused correctly by the server surfaced as a bare failure, because the
 * sentence explaining it lives here.
 *
 *  1. **A refusal re-reads.** `onApiFailure` fires on every refused `request()`,
 *     and a tenant-state code re-reads immediately. This is the case that
 *     matters: the explanation now arrives WITH the refusal instead of up to
 *     90 s later. It is the generalisation of the single hand-wired call site
 *     `DriveView` already had for the upload path.
 *  2. **Returning to the tab re-reads.** Note the SPA sets
 *     `refetchOnWindowFocus: false` globally, and deliberately — the comment in
 *     `main.tsx` is about not re-fanning-out 200 requests. That reasoning is
 *     about a query CACHE with hundreds of entries; this is one request for one
 *     boolean pair, so it is not the thing that setting refuses. (This hook is
 *     not a TanStack query at all, so the global setting never applied to it
 *     either way — a real source of confusion worth stating.)
 *
 * Neither replaces the poll: an idle, focused, untouched tab still converges
 * only on the interval. A realtime push would close that last gap and is the
 * deliberate next step, not an oversight.
 *
 * A missing or failed read resolves to NOT paused. That is the same fail-open
 * stance `isTenantWriteFrozen` takes on the server and for the same reason: this
 * is an explanatory banner, and a transient error must not tell every customer on
 * a healthy deployment that their uploads are paused.
 */
import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react'
import { onApiFailure, request, isSessionEndedError } from '@papan/api-client'
import { IDENTITY_ONLY_CODE, TENANT_WRITE_FROZEN_CODE } from '@papan/types'

export interface WriteStatus {
  /** New files and folders are refused. Reads and edits are unaffected. */
  uploadsPaused: boolean
  /**
   * This tenant is identity-only (0175) — not a workspace at all, so there is
   * no drive to write into or read from.
   *
   * ORTHOGONAL to `uploadsPaused`, and the two must never be collapsed: a
   * tenant can be in either, both or neither, and folding them together makes
   * whichever ranks lower invisible. The single `code`/`message` pair below has
   * a precedence (identity-only wins) precisely because it can only carry one
   * reason; these two booleans always both mean what they say.
   *
   * This is the SELF-CORRECTING source of the mode for the shell: it is polled,
   * and `refresh()` re-reads it, so a platform admin's mid-session change
   * narrows or restores the surface without a reload. §8 asks for exactly that
   * ("react to the stable 403 by refreshing session mode").
   */
  identityOnly: boolean
  /** `TENANT_WRITE_FROZEN` when paused. Branch on this, never on `message`. */
  code?: string
  /** The server's sentence. English-only by design — a localized SPA should
   *  render its own copy keyed on `code` instead of printing this. */
  message?: string
  /** Re-read now. Call from a 402 handler so the explanation appears with the
   *  refusal rather than up to a poll interval later. */
  refresh: () => void
}

const NOT_PAUSED: WriteStatus = { uploadsPaused: false, identityOnly: false, refresh: () => {} }

const WriteStatusContext = createContext<WriteStatus>(NOT_PAUSED)

/** Longer than the server's 60 s freeze cache — polling faster than the truth
 *  can change buys nothing but requests. */
const POLL_MS = 90_000

/** The route this hook reads. Named so a refusal ON it can be ignored — a
 *  listener that re-reads the endpoint whose failure woke it is a loop. */
const WRITE_STATUS_PATH = '/api/files/write-status'

/**
 * Refusals that are statements about the TENANT, not about the request.
 *
 * Exactly the two states this hook reports, so the set cannot drift from what a
 * re-read could possibly change. A 402 is deliberately NOT in here as a bare
 * status: plenty of them are about credit, and re-reading on every one would
 * make a spending cap poll this endpoint.
 */
const TENANT_STATE_CODES = new Set<string>([TENANT_WRITE_FROZEN_CODE, IDENTITY_ONLY_CODE])

export function WriteStatusProvider({ tenantId, children }: {
  /** The ACTIVE tenant. Changing it re-reads, because the freeze is per tenant
   *  and a stale answer would describe the org the user just left. */
  tenantId: string | null | undefined
  children: ReactNode
}) {
  const [state, setState] = useState<{
    uploadsPaused: boolean; identityOnly: boolean; code?: string; message?: string
  }>({
    uploadsPaused: false,
    identityOnly: false,
  })
  // Guards against a slow response for a PREVIOUS tenant landing after a switch
  // and painting that org's banner over this one.
  const forTenant = useRef<string | null | undefined>(tenantId)

  const load = useCallback(async () => {
    const asked = tenantId
    if (!asked) { setState({ uploadsPaused: false, identityOnly: false }); return }
    try {
      const r = await request<{
        uploadsPaused?: boolean; identityOnly?: boolean; code?: string; message?: string
      }>('/api/files/write-status')
      if (forTenant.current !== asked) return
      setState({
        uploadsPaused: !!r.uploadsPaused,
        // Absent ⇒ false, the same fail-open direction as `uploadsPaused` and
        // for the same reason: an older API omits the field during a rolling
        // restart, and claiming identity-only there would take the drive away
        // from every user on a healthy deployment.
        identityOnly: !!r.identityOnly,
        code: r.code,
        message: r.message,
      })
    } catch (err) {
      // #1316: a read dropped at a session end is no answer — the last known state stays (a same-tenant identity switch
      // is under the same tenant's freeze; failing open here cleared a correct "Uploads paused" until the next poll)
      // …and the read is asked again NOW, so a new tenant's status (after a switch) is read as that tenant, not left
      // as the previous one's until the 90 s poll. A dropped read means the session ended after it started; the
      // re-read starts after that end, so it cannot be dropped for the same end — no loop.
      if (isSessionEndedError(err)) {
        if (forTenant.current === asked) queueMicrotask(() => reReadRef.current())
        return
      }
      // Fail open — see the note above. An unreachable endpoint must not claim a
      // freeze, and an older server without this route 404s.
      if (forTenant.current === asked) setState({ uploadsPaused: false, identityOnly: false })
    }
  }, [tenantId])

  /**
   * One read at a time, and never a dropped trigger.
   *
   * A refused listing fans out — a drive view issues several requests, so a
   * single flip can produce a burst of 403s within a few milliseconds, and one
   * `load()` each would be a burst of reads for one answer. Coalescing by a TIME
   * window would be simpler and subtly wrong: a trigger arriving while a read is
   * already in flight may be describing state NEWER than the one that read will
   * return, so discarding it can settle on the stale answer and stay there until
   * the poll. Tracking "asked again while busy" instead drops nothing — it costs
   * at most one extra read and always ends on a value fetched after the last
   * trigger.
   *
   * Terminates because the only triggers are the poll, a tenant change, a
   * refusal on some OTHER path, and a tab focus — `load()` itself cannot produce
   * one.
   */
  const inFlight = useRef(false)
  const askedAgain = useRef(false)
  const reReadRef = useRef<() => void>(() => {})
  const loadCoalesced = useCallback(() => {
    if (inFlight.current) { askedAgain.current = true; return }
    const run = (): void => {
      inFlight.current = true
      void load().finally(() => {
        inFlight.current = false
        if (askedAgain.current) { askedAgain.current = false; run() }
      })
    }
    run()
  }, [load])
  reReadRef.current = loadCoalesced

  useEffect(() => {
    forTenant.current = tenantId
    void load()
    const id = setInterval(() => void load(), POLL_MS)
    return () => clearInterval(id)
  }, [tenantId, load])

  /**
   * A refusal that names a tenant state re-reads that state at once, so the
   * explanation reaches the screen with the refusal rather than a poll later.
   *
   * Ignores refusals on its OWN route for the obvious reason, and that guard is
   * kept even though the server exempts the route from the identity-only read
   * gate: this side must not depend on the other side continuing to.
   */
  useEffect(() => {
    return onApiFailure(({ code, path }) => {
      if (!code || !TENANT_STATE_CODES.has(code)) return
      if (path.startsWith(WRITE_STATUS_PATH)) return
      loadCoalesced()
    })
  }, [loadCoalesced])

  /**
   * Coming back to the tab re-reads.
   *
   * Both events, because they answer different questions: `visibilitychange`
   * catches a switch between tabs, `focus` catches a switch between windows with
   * this tab already visible. They overlap, which the coalescer absorbs.
   */
  useEffect(() => {
    const onBack = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      loadCoalesced()
    }
    document.addEventListener('visibilitychange', onBack)
    window.addEventListener('focus', onBack)
    return () => {
      document.removeEventListener('visibilitychange', onBack)
      window.removeEventListener('focus', onBack)
    }
  }, [loadCoalesced])

  return (
    <WriteStatusContext.Provider value={{ ...state, refresh: loadCoalesced }}>
      {children}
    </WriteStatusContext.Provider>
  )
}

/**
 * The active tenant's write status.
 *
 * Safe outside the provider: returns "not paused", so a surface that renders in a
 * context without it (a share view, an unauthenticated shell) shows nothing rather
 * than crashing. That is deliberate — this is an explanatory banner, and it must
 * never be load-bearing for anything.
 */
export function useWriteStatus(): WriteStatus {
  return useContext(WriteStatusContext)
}
